import { useInfiniteQuery } from "@tanstack/react-query";
import NewsContentGrid from "./components/NewsContentGrid";
import { ScreenLayout } from "../../components/layouts/components/ScreenLayout";
import { Flex, Spinner } from "@chakra-ui/react";
import { getNextPageParam, getPreviousPageParam } from "../../utils/api";
import { CollapsibleNavBar } from "../../components/navbar/CollapsibleNavBar";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { NewsCategoriesCarousel } from "./components/NewsCategoriesCarousel";
import { useAccountData } from "../../hooks/useAccountData";
import { getNews } from "./api/newsScreenApi";
import { useNewsCategories } from "./hooks/useNewsCategories";
import { BrowseEmptyState } from "../browse/components/EmptyState";
import { useScrollVisibility } from "../../hooks/useScrollVisibility";
import { useEffect } from "react";
import { t } from "i18next";
import { useSearchParams } from "react-router-dom";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";

const GET_NEWS_QUERY_KEY = "GET_NEWS_QUERY_KEY";
export default function NewsScreen() {
  const accountData = useAccountData();
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const topBarHeight = useTopBarHeight();

  const [urlSearchParams] = useSearchParams();
  const tag = urlSearchParams.get("tag") ?? "";

  const { activeCategory } = useNewsCategories();

  const isVideoHub = activeCategory?.slug === "video-hub";

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      enabled: !!accountData,
      queryFn: (ctx) => {
        const pageParam = ctx.pageParam;
        return getNews({ categoryId: activeCategory?.id, pageParam, tag });
      },
      queryKey: [
        GET_NEWS_QUERY_KEY,
        activeCategory,
        accountData?.user?.id,
        tag,
      ],
      initialPageParam: 1,
      getNextPageParam,
      getPreviousPageParam,
    });

  // Scroll handler
  useEffect(() => {
    const container = window.document.documentElement;

    const handleScroll = () => {
      if (!container || isFetchingNextPage || !hasNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (isBottom) {
        fetchNextPage();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage]);

  useTitleOnNavigation(
    isVideoHub ? t("screens.hub.videos.title") : t("screens.hub.news.title"),
  );

  const pages = data?.pages;
  const articles = pages?.flatMap((p) => p.data.payload.results);

  const { isHidden } = useScrollVisibility();

  if (!accountData) return null;

  return (
    <Flex gap={isSmallBreakpoint ? 0 : "30px"} direction="column" width="full">
      <CollapsibleNavBar
        isCollapsed={isHidden}
        shouldCollapse={isSmallBreakpoint}
        boxProps={{
          paddingRight: isSmallBreakpoint ? 0 : "40px",
          maxWidth: "1200px",
          width: "100%",
          alignSelf: "center",
          top: `${topBarHeight.get()}px`,
          position: "sticky",
          bg: "charcoal.charcoal",
          pb: "10px",
          zIndex: 1,
        }}
        nonCollapsibleChildren={
          isVideoHub ? null : (
            <Flex
              justifyContent="space-between"
              pl={isSmallBreakpoint ? 4 : 0}
              pr={isSmallBreakpoint ? 4 : 6}
            >
              <NewsCategoriesCarousel />
            </Flex>
          )
        }
      >
        <ScreenLayout
          pageTitle={
            isVideoHub
              ? t("screens.hub.videos.title")
              : t("screens.hub.news.title")
          }
          hideSearch
        />
      </CollapsibleNavBar>
      {!data || isLoading || !articles?.length ? (
        <Flex
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          {data && !isLoading ? (
            <BrowseEmptyState />
          ) : (
            <Spinner color="blaze.blaze" />
          )}
        </Flex>
      ) : (
        <NewsContentGrid
          articles={articles ?? []}
          isFetchingNextPage={isFetchingNextPage}
          isVideoHub={isVideoHub}
        />
      )}
    </Flex>
  );
}
